import React, { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { PlusCircle, Activity as ActivityIcon, User as UserIcon, Loader2 } from "lucide-react";

const ACTION_LABEL = {
  created: "Created",
  updated: "Updated",
  imported: "Imported",
  note: "Note",
  compliance_created: "Compliance created",
  compliance_updated: "Compliance updated",
  compliance_deleted: "Compliance deleted",
};

function formatWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
  const mm = MONTHS[d.getUTCMonth()];
  const yyyy = d.getUTCFullYear();
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const min = String(d.getUTCMinutes()).padStart(2, "0");
  return `${dd}-${mm}-${yyyy} ${hh}:${min} UTC`;
}

export default function ClientActivityDialog({ open, onClose, client }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    if (!client) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/clients/${client.id}/activities`);
      setActivities(data);
    } finally { setLoading(false); }
  }, [client]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const addNote = async () => {
    if (!note.trim()) return;
    setAdding(true);
    try {
      await api.post(`/clients/${client.id}/activities`, { description: note.trim(), action: "note" });
      setNote("");
      toast.success("Note added");
      load();
    } finally { setAdding(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-display" data-testid="activity-title">
            Activity · {client?.name || ""}
          </DialogTitle>
          <DialogDescription>Timeline of actions and notes recorded against this client.</DialogDescription>
        </DialogHeader>

        <div className="border border-slate-200 rounded-lg p-3 bg-slate-50 space-y-2" data-testid="activity-add">
          <div className="text-xs uppercase tracking-widest text-slate-600 font-semibold flex items-center gap-1.5">
            <PlusCircle className="w-3.5 h-3.5" /> Add a note
          </div>
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Called client for missing invoice…" data-testid="activity-note-input" />
          <div className="flex justify-end">
            <Button size="sm" onClick={addNote} disabled={!note.trim() || adding} data-testid="activity-note-add">
              {adding && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />} Add note
            </Button>
          </div>
        </div>

        <div className="mt-4 overflow-y-auto flex-1 pr-1">
          {loading ? (
            <div className="text-sm text-slate-500 p-4">Loading…</div>
          ) : activities.length === 0 ? (
            <div className="text-sm text-slate-500 p-8 text-center">No activity yet.</div>
          ) : (
            <ul className="relative pl-6">
              <span className="absolute left-2 top-1 bottom-1 w-px bg-slate-200" aria-hidden />
              {activities.map((a) => (
                <li key={a.id} className="relative py-3" data-testid={`activity-${a.id}`}>
                  <span className="absolute -left-[13px] top-4 w-3.5 h-3.5 rounded-full bg-white border-2 border-blue-500" />
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-700 font-semibold">
                      {ACTION_LABEL[a.action] || a.action}
                    </span>
                    <span className="text-xs text-slate-500 font-mono-num">{formatWhen(a.timestamp)}</span>
                  </div>
                  <div className="mt-1 text-sm text-slate-900">{a.description}</div>
                  <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                    <UserIcon className="w-3 h-3" /> {a.user_name || "system"}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
